import { useEffect, useState } from 'react'
import { api } from '../../api'
import { getCached, setCached } from '../../lib/swr'
import CheckoutPayment from '../CheckoutPayment'
import WalletIcon from '../WalletIcon'
import FactureComModal from './FactureComModal'
import { ORANGE, TEAL, VERT, VERT_OK, eur, fmtDateLongue } from './marcheProducer'
import { avecAlpha } from '../../lib/couleurs'
import { MVP } from '../../mvp'

/**
 * Marche producteur — onglet « Historique » (proto v7.2.1, mHistoP).
 * Cartes 1 evenement = 1 statut (CA a saisir / Commission a regler / Regle) +
 * bandeau de relance, puis ecran detail : saisie/rappel du CA declare,
 * commission Manrina (JAMAIS « regler le CA » — v7.1 §4.4) et REGLEMENT
 * (ADR-058 §5) : wallet (compense sur le solde), carte (Payment Element),
 * virement/especes (declares -> en attente de confirmation Manrina au BO).
 */

// TVA 8,5 % sur la commission seule, EN DEDANS (arbitrage Enzo 3 juil) : la
// commission de 10 % est TTC — le du = CA × taux, la TVA en est extraite.
const TVA_COMMISSION = 0.085

const STATUT = {
  ca_a_saisir: { l: 'CA à saisir', e: '✍️', c: ORANGE },
  a_regler: { l: 'Commission à régler', e: '⏳', c: '#4b4fb0' },
  regle: { l: 'Réglé', e: '✓', c: VERT_OK },
}

const statutDe = (p) => (p.ca == null ? 'ca_a_saisir' : p.paye ? 'regle' : 'a_regler')

// Cache SWR (S29, E-13) : l'historique deja vu se reaffiche sans squelette.
const CLE_HISTORIQUE = 'producer/history'

function MarcheProducerHisto({ onToast, initialSelId }) {
  const [histo, setHisto] = useState(() => getCached(CLE_HISTORIQUE) || [])
  const [loading, setLoading] = useState(() => getCached(CLE_HISTORIQUE) === undefined)
  const [sel, setSel] = useState(null) // participation selectionnee (detail)
  const [factureVue, setFactureVue] = useState(false) // facture COM plein ecran (v7.2.3)
  const [caSaisie, setCaSaisie] = useState('')
  const [saving, setSaving] = useState(false)
  // Reglement (ADR-058 §5)
  const [reglMode, setReglMode] = useState(null) // WALLET | CARTE | VIREMENT | ESPECES
  const [paying, setPaying] = useState(false)
  const [cardPaiement, setCardPaiement] = useState(null) // { clientSecret, publishableKey, paymentIntentId, montant }
  const [soldeWallet, setSoldeWallet] = useState(null)

  // Solde producteur (mode wallet) — rafraichi a l'ouverture de l'onglet.
  // Wallet hors MVP (S62) : pas de lecture, et le mode « Mon solde » n'apparait pas.
  useEffect(() => {
    if (!MVP.WALLET) return
    api.getWallet().then((w) => setSoldeWallet(Number(w?.solde?.producteur) || 0)).catch(() => {})
  }, [])

  const majParticipation = (id, patch) =>
    setHisto((hs) => {
      const maj = hs.map((x) => (x.id === id ? { ...x, ...patch } : x))
      setCached(CLE_HISTORIQUE, maj)
      return maj
    })
  const refreshWallet = () =>
    api.getWallet().then((w) => {
      setCached('wallet/me', w)
      setSoldeWallet(Number(w?.solde?.producteur) || 0)
    }).catch(() => {})

  useEffect(() => {
    api.producer.getHistory()
      .then((h) => {
        setHisto(h || [])
        setCached(CLE_HISTORIQUE, h || [])
        // Navigation croisee depuis « Ma facturation » : ouvrir directement le
        // detail demande (garde : uniquement s'il existe dans l'historique charge).
        if (initialSelId && (h || []).some((x) => x.id === initialSelId)) setSel(initialSelId)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [initialSelId])

  if (loading) return <div className="h-40 rounded-3xl animate-pulse bg-ink/5" />

  // ===== DETAIL D'UN MARCHE PASSE =====
  if (sel) {
    const p = histo.find((x) => x.id === sel) || sel
    const marche = p.marche || p.event || {}
    const st = statutDe(p)
    const taux = Number(p.tauxCommission) || 0
    const ca = st === 'ca_a_saisir' ? parseFloat((caSaisie || '0').replace(',', '.')) || 0 : Number(p.ca)
    // Le du TTC = CA × taux ; la TVA est EN DEDANS (extraite, pas ajoutee)
    const commission = st === 'ca_a_saisir' ? (ca * taux) / 100 : Number(p.montantCommission) || (ca * taux) / 100
    const tva = Math.round((commission - commission / (1 + TVA_COMMISSION)) * 100) / 100

    const validerCa = async () => {
      setSaving(true)
      try {
        const maj = await api.producer.submitCa(p.id, Math.round(ca * 100) / 100)
        setHisto((hs) => {
          const suite = hs.map((x) => (x.id === p.id ? { ...x, ...maj } : x))
          setCached(CLE_HISTORIQUE, suite)
          return suite
        })
        onToast?.('CA déclaré — commission calculée ✓')
        refreshWallet() // la dette COMMISSION_DUE vient d'etre ecrite au ledger
      } catch (e) {
        onToast?.(e.message || 'La déclaration du CA a échoué.')
      } finally {
        setSaving(false)
      }
    }

    const ttc = Math.round(commission * 100) / 100 // la commission EST le TTC
    const enAttenteManrina = !p.paye && !!p.modePaiement && ['VIREMENT', 'ESPECES'].includes(p.modePaiement)

    const regler = async () => {
      setPaying(true)
      try {
        if (reglMode === 'CARTE') {
          const res = await api.producer.payCommissionCard(p.id)
          if (!res?.clientSecret) throw new Error('Paiement carte indisponible pour le moment.')
          setCardPaiement(res)
        } else {
          const res = await api.producer.payCommission(p.id, reglMode)
          if (res?.regle) {
            majParticipation(p.id, { paye: true, modePaiement: 'AVOIR' })
            onToast?.(`Commission ${eur(res.montant)} réglée avec ton solde wallet ✓`)
            refreshWallet()
          } else {
            majParticipation(p.id, { modePaiement: reglMode })
            onToast?.(reglMode === 'VIREMENT' ? 'Virement déclaré — en attente de confirmation par Manrina.' : 'Règlement en espèces déclaré — à remettre à Manrina.')
          }
          setReglMode(null)
        }
      } catch (e) {
        onToast?.(e.message || 'Le règlement a échoué — réessaie.')
      } finally {
        setPaying(false)
      }
    }

    // Verification active du paiement carte (meme filet que la recharge wallet)
    const verifierCarte = (paymentIntentId, montant) => {
      let tentatives = 0
      const boucle = () => {
        api.producer.verifyCommissionPayment(p.id, paymentIntentId)
          .then((r) => {
            if (r?.regle) {
              majParticipation(p.id, { paye: true, modePaiement: 'CARTE_EN_LIGNE' })
              onToast?.(`✓ Commission ${eur(montant)} réglée par carte`)
              refreshWallet()
            } else if (tentatives < 3) { tentatives += 1; setTimeout(boucle, 3000) }
            else onToast?.(`Paiement reçu (statut Stripe : ${r?.statut || '?'}) — le règlement suivra sa confirmation.`)
          })
          .catch((e) => {
            console.error('Verification reglement commission en echec :', e)
            if (tentatives < 3) { tentatives += 1; setTimeout(boucle, 3000) }
            else onToast?.('Paiement validé, mais la vérification ne répond pas — préviens Manrina si le statut ne change pas.')
          })
      }
      boucle()
    }

    // ---- Vue paiement CARTE en pleine page (meme pattern que la recharge) ----
    if (cardPaiement) {
      return (
        <>
          <button onClick={() => setCardPaiement(null)} className="bg-transparent border-none text-[13px] font-semibold mb-3 text-ink/55">
            ‹ Régler ma commission
          </button>
          <p className="text-[11px] font-bold uppercase mb-2.5 mx-0.5" style={{ letterSpacing: 2, color: TEAL }}>
            Commission — {eur(cardPaiement.montant)}
          </p>
          <CheckoutPayment
            clientSecret={cardPaiement.clientSecret}
            publishableKey={cardPaiement.publishableKey}
            amount={cardPaiement.montant}
            onBack={() => setCardPaiement(null)}
            onSuccess={() => {
              const { paymentIntentId, montant } = cardPaiement
              setCardPaiement(null)
              setReglMode(null)
              onToast?.('Paiement validé — vérification…')
              verifierCarte(paymentIntentId, montant)
            }}
          />
        </>
      )
    }

    return (
      <>
        <button onClick={() => { setSel(null); setCaSaisie(''); setFactureVue(false) }} className="bg-transparent border-none text-[13px] font-semibold mb-3 text-ink/55">
          ‹ Historique
        </button>
        <div className="glass-dark rounded-3xl relative overflow-hidden mb-4" style={{ padding: 22, color: 'rgb(var(--c-creme))' }}>
          <div className="absolute -top-10 -right-10 w-[150px] h-[150px] rounded-full pointer-events-none" style={{ background: 'radial-gradient(circle, rgba(20,184,166,0.4), transparent 70%)' }} />
          <p className="font-serif text-[22px] relative">{marche.nom || marche.lieu}</p>
          <p className="text-[12.5px] mt-1 relative" style={{ color: 'rgba(247,240,234,0.75)' }}>
            📅 {fmtDateLongue(marche.date)} · 📍 {marche.lieu}
          </p>
        </div>

        {/* 1) SAISIE / RAPPEL DU CA */}
        <p className="text-[11px] font-bold uppercase mb-2 mx-0.5" style={{ letterSpacing: 2, color: TEAL }}>
          Votre chiffre d'affaires
        </p>
        <div className="glass-surface rounded-[20px] mb-4 p-4">
          <p className="text-xs leading-relaxed mb-2.5 text-ink/60">
            🦊 Vous encaissez vos ventes vous-même au marché. Déclarez votre total : je calcule{' '}
            <strong>uniquement la commission Manrina</strong> à régler.
          </p>
          <div className="flex items-center gap-2.5">
            <input
              value={st === 'ca_a_saisir' ? caSaisie : String(Number(p.ca)).replace('.', ',')}
              onChange={(e) => setCaSaisie(e.target.value.replace(/[^0-9.,]/g, ''))}
              disabled={st !== 'ca_a_saisir'}
              inputMode="decimal"
              placeholder="0,00"
              className="flex-1 font-serif font-bold rounded-[13px] outline-none"
              style={{
                fontSize: 18,
                padding: '13px 15px',
                border: '1px solid rgba(7,62,61,0.15)',
                background: st !== 'ca_a_saisir' ? 'rgba(7,62,61,0.05)' : 'rgba(255,255,255,0.8)',
                color: VERT,
              }}
            />
            <span className="font-serif text-lg font-bold" style={{ color: VERT }}>€</span>
          </div>
          {st === 'ca_a_saisir' && (
            <button
              disabled={ca <= 0 || saving}
              onClick={validerCa}
              className="mt-3 w-full rounded-2xl text-sm font-extrabold"
              style={{ padding: 13, border: 'none', background: ca > 0 && !saving ? VERT : 'rgba(7,62,61,0.18)', color: 'rgb(var(--c-creme))' }}
            >
              {saving ? 'Envoi…' : 'Valider mon CA'}
            </button>
          )}
        </div>

        {/* 2) COMMISSION (une fois le CA saisi) */}
        {st !== 'ca_a_saisir' && (
          <>
            <p className="text-[11px] font-bold uppercase mb-2 mx-0.5" style={{ letterSpacing: 2, color: TEAL }}>
              Commission Manrina
            </p>
            <div className="glass-surface rounded-[20px] mb-4 p-4">
              {[
                ['CA déclaré (encaissé par vous)', eur(p.ca)],
                [`Commission Manrina (${taux}% TTC)`, eur(commission)],
                ['dont TVA 8,5%', eur(tva)],
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between items-baseline py-1">
                  <span className="text-[12.5px] text-ink/60">{k}</span>
                  <span className="font-serif text-[13px] font-bold" style={{ color: VERT }}>{v}</span>
                </div>
              ))}
              <div className="h-px my-2" style={{ background: 'rgba(7,62,61,0.1)' }} />
              <div className="flex justify-between items-baseline">
                <span className="text-[13.5px] font-extrabold" style={{ color: VERT }}>À régler à Manrina</span>
                <span className="font-serif text-[19px] font-extrabold" style={{ color: ORANGE }}>{eur(commission)}</span>
              </div>
              {p.factureCommission?.numero && (
                <>
                  <p className="text-[10.5px] mt-2 text-ink/45">Facture {p.factureCommission.numero}</p>
                  <button
                    onClick={() => setFactureVue(true)}
                    className="mt-2.5 w-full rounded-[14px] text-[13px] font-extrabold"
                    style={{ padding: 12, border: `1px solid ${avecAlpha(ORANGE, 0.33)}`, background: 'rgba(244,137,83,0.08)', color: ORANGE }}
                  >
                    🧾 Voir la facture {p.factureCommission.numero}
                  </button>
                </>
              )}
            </div>

            {/* 3) REGLEMENT (ADR-058 §5) — toujours « regler MA COMMISSION », jamais le CA */}
            {st === 'regle' ? (
              <div className="rounded-[18px] p-4 flex gap-3 items-center" style={{ background: 'rgba(10,138,95,0.08)', border: '1px solid rgba(10,138,95,0.3)' }}>
                <span
                  className="w-9 h-9 rounded-full flex-shrink-0 flex items-center justify-center text-base font-extrabold"
                  style={{ background: 'rgba(10,138,95,0.15)', border: '1px solid rgba(10,138,95,0.4)', color: '#0a6b52' }}
                >
                  ✓
                </span>
                <p className="flex-1 text-[12.5px] font-bold leading-relaxed" style={{ color: '#0a6b52' }}>
                  Commission réglée
                  {p.modePaiement === 'AVOIR' ? ' avec ton solde wallet' : p.modePaiement ? ` (${String(p.modePaiement).replace('_', ' ').toLowerCase()})` : ''}. Merci !
                </p>
              </div>
            ) : enAttenteManrina ? (
              <div className="glass-surface rounded-[18px] p-4 flex gap-3 items-center" style={{ background: 'rgba(244,137,83,0.08)', border: `1px solid ${avecAlpha(ORANGE, 0.33)}` }}>
                <span className="text-[22px]">⏳</span>
                <p className="flex-1 text-[12.5px] font-semibold leading-relaxed" style={{ color: VERT }}>
                  Règlement par {p.modePaiement === 'VIREMENT' ? 'virement' : 'espèces'} déclaré —{' '}
                  <strong>en attente de confirmation par Manrina</strong>.
                </p>
              </div>
            ) : (
              <>
                <p className="text-[11px] font-bold uppercase mb-2 mx-0.5" style={{ letterSpacing: 2, color: TEAL }}>
                  Régler ma commission
                </p>
                <div className="flex flex-col gap-2 mb-3.5">
                  {[
                    ...(MVP.WALLET ? [{
                      k: 'WALLET', e: null, l: 'Mon solde wallet', // WalletIcon SVG, jamais l'emoji
                      d: soldeWallet == null ? 'Solde en cours de lecture…' : soldeWallet >= ttc ? `${eur(soldeWallet)} disponibles — validé immédiatement` : `Solde insuffisant (${eur(soldeWallet)})`,
                      off: soldeWallet != null && soldeWallet < ttc,
                    }] : []),
                    { k: 'CARTE', e: '💳', l: 'Carte bancaire', d: 'Validé immédiatement' },
                    { k: 'VIREMENT', e: '🏦', l: 'Virement', d: 'Validé à réception par Manrina' },
                    { k: 'ESPECES', e: '💶', l: 'Espèces', d: 'Remise en main propre à confirmer' },
                  ].map((m) => {
                    const on = reglMode === m.k
                    return (
                      <button
                        key={m.k}
                        disabled={m.off}
                        onClick={() => setReglMode(m.k)}
                        className="rounded-2xl flex items-center gap-3 text-left"
                        style={{
                          padding: '13px 14px',
                          border: on ? `1.5px solid ${TEAL}` : '1px solid rgba(7,62,61,0.1)',
                          background: on ? 'rgba(20,184,166,0.08)' : 'rgba(255,255,255,0.5)',
                          opacity: m.off ? 0.5 : 1,
                        }}
                      >
                        <span className="text-[22px] flex-shrink-0 flex items-center justify-center" style={{ minWidth: 24 }}>
                          {m.e || <WalletIcon size={22} color="#0d6b5f" />}
                        </span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-bold" style={{ color: VERT }}>{m.l}</p>
                          <p className="text-[11px] text-ink/50">{m.d}</p>
                        </div>
                        <span
                          className="w-[22px] h-[22px] rounded-full flex-shrink-0 flex items-center justify-center text-white text-[13px] font-extrabold"
                          style={{ border: on ? 'none' : '2px solid rgba(7,62,61,0.25)', background: on ? TEAL : 'transparent' }}
                        >
                          {on ? '✓' : ''}
                        </span>
                      </button>
                    )
                  })}
                </div>
                <button
                  disabled={!reglMode || paying}
                  onClick={regler}
                  className="w-full rounded-2xl text-[15px] font-extrabold"
                  style={{ padding: 15, border: 'none', background: reglMode && !paying ? ORANGE : 'rgba(7,62,61,0.18)', color: VERT }}
                >
                  {paying
                    ? 'Règlement…'
                    : reglMode === 'WALLET'
                      ? `Payer ${eur(ttc)} avec mon solde`
                      : reglMode === 'CARTE'
                        ? `Payer ${eur(ttc)}`
                        : reglMode === 'VIREMENT'
                          ? "J'ai effectué le virement"
                          : reglMode === 'ESPECES'
                            ? 'Je règle en espèces'
                            : 'Choisir un mode'}
                </button>
              </>
            )}
          </>
        )}

        {/* Facture COM plein ecran (proto v7.2.3 — snapshot ModuleInvoice) */}
        {factureVue && p.factureCommission && (
          <FactureComModal
            facture={p.factureCommission}
            eventNom={marche.nom || marche.lieu}
            eventDate={marche.date}
            enAttenteManrina={enAttenteManrina}
            onClose={() => setFactureVue(false)}
            onToast={onToast}
          />
        )}
      </>
    )
  }

  // ===== LISTE DES MARCHES PASSES =====
  const aSaisir = histo.filter((p) => statutDe(p) === 'ca_a_saisir')
  return (
    <>
      {aSaisir.length > 0 && (
        <div
          className="glass-surface rounded-[18px] mb-3.5 flex gap-3 items-center"
          style={{ padding: '14px 16px', background: 'linear-gradient(120deg, rgba(255,255,255,0.6), rgba(244,137,83,0.16))', border: `1px solid ${avecAlpha(ORANGE, 0.33)}` }}
        >
          <span className="text-[22px]">📣</span>
          <p className="flex-1 text-[12.5px] font-semibold leading-relaxed" style={{ color: VERT }}>
            <strong>{aSaisir.length} marché{aSaisir.length > 1 ? 's' : ''}</strong> en attente : déclarez votre chiffre
            d'affaires pour calculer votre commission.
          </p>
        </div>
      )}
      <p className="text-[11px] font-bold uppercase mb-2.5 mx-0.5" style={{ letterSpacing: 2, color: TEAL }}>
        Mes marchés passés ({histo.length})
      </p>
      {histo.length === 0 && (
        <div className="glass-surface rounded-3xl p-6 text-center">
          <span className="text-4xl block mb-2">🧾</span>
          <p className="text-[13px] text-ink/65">Aucun marché passé pour le moment.</p>
        </div>
      )}
      <div className="flex flex-col gap-2.5">
        {histo.map((p) => {
          const marche = p.marche || p.event || {}
          const st = STATUT[statutDe(p)]
          return (
            <button
              key={p.id}
              onClick={() => { setSel(p.id); setCaSaisie('') }}
              className="glass-surface rounded-[20px] flex items-center gap-3 text-left"
              style={{ padding: '14px 16px', borderLeft: `4px solid ${st.c}` }}
            >
              <span
                className="w-11 h-11 rounded-2xl flex-shrink-0 flex items-center justify-center text-[19px]"
                style={{ background: 'linear-gradient(135deg,rgb(var(--c-teal)),rgb(var(--c-green)))' }}
              >
                {/* Icone du TYPE d'evenement (ADR-062) */}
                {marche.typeConfig?.icone || '🎪'}
              </span>
              <div className="flex-1 min-w-0">
                <p className="text-[13.5px] font-bold" style={{ color: VERT }}>{marche.nom || marche.lieu}</p>
                <p className="text-[11px] text-ink/50">
                  {fmtDateLongue(marche.date)}
                  {p.ca != null ? ` · CA ${eur(p.ca)}` : ''}
                  {p.paye && p.factureCommission?.numero ? ` · 🧾 ${p.factureCommission.numero}` : ''}
                </p>
              </div>
              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                <span className="text-[10.5px] font-extrabold rounded-full px-2.5 py-1" style={{ color: st.c, background: avecAlpha(st.c, 0.1) }}>
                  {st.e} {st.l}
                </span>
                <span className="text-[13px] text-ink/30">›</span>
              </div>
            </button>
          )
        })}
      </div>
    </>
  )
}

export default MarcheProducerHisto
